// askSmart-ui/src/pages/TOSP/tabs/PastTenantsTab.tsx
import React, { useMemo, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import TenantModal, { TenantFormValues } from "../components/TenantModal";
import TenantGrid, { PAST_TENANT_COLUMNS } from "../components/TenantGrid";
import { useTospData } from "../TospDataContext";
import { newId, pastTenants, Tenant, tenantLabel } from "../types";

// Past Tenants — the businesses that have left, and the record of what they
// held while they were here.
//
// They are here rather than deleted because a unit's history IS the leases that
// have demised it: the rent a unit produced in 2024, the sales reported against
// it, the taxes recovered on it. Delete the tenancy and the budget's prior year
// goes blank for that unit.
//
// Which is also why they can be ADDED here. A 2025 or 2026 budget wants last
// year's figures for every unit, and for a unit that changed hands those figures
// belong to somebody who was gone before any of this was recorded. Entering them
// after the fact is the only way the comparison is complete.
export default function PastTenantsTab() {
  const { units, tenants, unitById, saveTenant, deleteTenant } = useTospData();
  // `null` for a new record, a tenant for an edit, `undefined` when closed — so
  // add and edit share one form without a second open/closed flag.
  const [editing, setEditing] = useState<Tenant | null | undefined>(undefined);
  const [viewing, setViewing] = useState<Tenant | null>(null);
  const [deleting, setDeleting] = useState<Tenant | null>(null);

  const past = useMemo(() => pastTenants(tenants), [tenants]);

  // No takeovers are possible here — the form doesn't offer them, because a
  // past tenancy takes nothing over — so the save is the tenant record alone.
  const handleSave = async ({ takeovers, ...values }: TenantFormValues) => {
    try {
      // A new record needs the fields the form doesn't ask for. Spelled out
      // rather than cast: a tenancy with no `delegates` array reads as
      // undefined everywhere that iterates it.
      const base: Tenant =
        editing ??
        ({
          id: newId("tnt"),
          previousContactEmail: "",
          contactTransferredAt: "",
          transferLog: [],
          delegates: [],
        } as unknown as Tenant);
      await saveTenant({ ...base, ...values });
      setEditing(undefined);
    } catch {
      /* surfaced by the provider */
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTenant(deleting.id);
      setDeleting(null);
    } catch {
      /* surfaced by the provider */
    }
  };

  return (
    <section className="tosp-card">
      <div className="tosp-toolbar">
        {/* Blue, not the usual green: adding a tenancy that has already ended is
            a record-keeping action, and giving it the same weight as "+ Add
            Tenant" on the live tab would invite somebody to start a new tenancy
            here by mistake. */}
        <button className="tosp-btn-outline-blue" onClick={() => setEditing(null)}>
          + Add Past Tenant
        </button>
      </div>

      <TenantGrid
        tenants={past}
        columns={PAST_TENANT_COLUMNS}
        emptyMessage='No past tenants yet. Click "+ Add Past Tenant" to record one, or a tenancy appears here once its lease has an end date in the past.'
        onView={setViewing}
        onEdit={setEditing}
        onDelete={setDeleting}
      />

      {/* Adding and editing. `pastTenant` skips the lease wizard, offers units
          another tenant now holds, and requires an end date in the past — see
          the prop's own note for why each of those is necessary. */}
      <TenantModal
        isOpen={editing !== undefined}
        tenant={editing ?? null}
        units={units}
        tenants={tenants}
        unitById={unitById}
        onClose={() => setEditing(undefined)}
        onSave={handleSave}
        pastTenant
      />

      <TenantModal
        isOpen={!!viewing}
        tenant={viewing}
        units={units}
        tenants={tenants}
        unitById={unitById}
        onClose={() => setViewing(null)}
        readOnly
      />

      <DeleteConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Past Tenant"
        message={
          deleting
            ? `Delete the record of ${tenantLabel(deleting)}? Their sales history and ` +
              `the rent their unit produced go with it, so a prior-year budget for that ` +
              `unit will read as blank.`
            : ""
        }
      />
    </section>
  );
}
